/**
 * The document-number format, mirrored from `DocumentNumberFormatter` in the domain.
 *
 * A deliberate copy of server logic, which is normally the wrong instinct — but this one earns it:
 * the alternative is showing a numbering series without showing what it produces, and a prefix,
 * a padding width and a separator are close to meaningless in a table until you see
 * `CH-2025-26-0001` next to them. The counter itself is never touched here; issuing a number is an
 * atomic UPDATE in the database, because a number handed out twice is a statutory problem.
 *
 * Keep this in step with `DocumentNumberFormatter.Format`. The pairing is covered by tests on both
 * sides using the same examples.
 */

export interface NumberingSeriesFormat {
  prefix: string;
  includeFinancialYear: boolean;
  paddingWidth: number;
  separator: string;
}

/**
 * The number a series would produce for a sequence value.
 *
 * Returns null rather than throwing on a non-positive sequence — the one deliberate difference
 * from the C#. This runs inside a table cell during render, and a screen that throws because a
 * counter is somehow zero is a worse answer than a cell that says nothing.
 */
export function formatDocumentNumber(
  series: NumberingSeriesFormat,
  financialYearCode: string | null | undefined,
  sequence: number,
): string | null {
  if (!Number.isFinite(sequence) || sequence <= 0) {
    return null;
  }

  const separator = series.separator ?? '';
  const segments: string[] = [];

  if (series.prefix.trim() !== '') {
    segments.push(series.prefix.trim());
  }

  if (series.includeFinancialYear && financialYearCode && financialYearCode.trim() !== '') {
    segments.push(financialYearCode.trim());
  }

  // A width of 0 or less means "do not pad" rather than an error — some series are genuinely
  // unpadded.
  segments.push(
    series.paddingWidth > 0
      ? String(sequence).padStart(series.paddingWidth, '0')
      : String(sequence),
  );

  return segments.join(separator);
}

/** What the next document out of this series will be called. */
export function nextDocumentNumber(
  series: NumberingSeriesFormat & { currentNumber: number },
  financialYearCode: string | null | undefined,
): string | null {
  return formatDocumentNumber(series, financialYearCode, series.currentNumber + 1);
}
